/**
 * Pure utility functions for schedule timeline rendering.
 * Shared between mobile (Expo) and web crew schedule components.
 */

const PIXELS_PER_MINUTE = 6;
const HOUR_HEIGHT = 60 * PIXELS_PER_MINUTE;

export { PIXELS_PER_MINUTE, HOUR_HEIGHT };

/** Mint green used for the live current-time marker across web + mobile. */
export const CURRENT_TIME_COLOR = "rgb(167, 243, 208)";

export interface HourMarker {
  /** Raw hour value (0-29 range; 24 = midnight, 25 = 01:00, etc.) */
  hour: number;
  /**
   * Canonical 24h label for the hour, e.g. "00:00" — the `% 24` wrap for the
   * after-midnight range lives here. Views re-format it for the reader's
   * 12h/24h setting (formatTimeWithSetting).
   */
  time: string;
  /** Pixel offset from the top of the timeline */
  topPosition: number;
}

export interface TimelineConfig {
  hours: HourMarker[];
  startHour: number;
  totalHeight: number;
}

/**
 * Determine whether any times in the list fall after midnight (hour < 6).
 */
export function hasAfterMidnightTimes(times: (string | null | undefined)[]): boolean {
  return times.some((t) => {
    if (!t) return false;
    const hour = parseInt(t.substring(0, 2));
    return hour < 6;
  });
}

/**
 * Generate hour markers for the timeline.
 * When assignments cross midnight, extends the timeline to 06:00 next day (hour 30).
 *
 * `pixelsPerMinute` lets callers render the same timeline at a different
 * vertical scale (pinch-to-zoom on mobile). It defaults to the module
 * constant so existing callers (and the web crew schedule) are unaffected.
 * The same value MUST be passed to `getPositionForTime` so within-hour
 * offsets line up with the scaled hour rows.
 */
export function generateHourMarkers(
  assignments: Array<{ filming_start: string; filming_end: string }> | undefined,
  fallbackHour?: number,
  pixelsPerMinute: number = PIXELS_PER_MINUTE,
): TimelineConfig {
  const hourHeight = 60 * pixelsPerMinute;
  let startHour = 8;

  if (assignments && assignments.length > 0) {
    const firstStart = assignments[0]?.filming_start;
    if (firstStart) {
      const h = parseInt(firstStart.substring(0, 2));
      // Times before 06:00 are "next day" — don't use them as startHour
      startHour = h < 6 ? 8 : h;
    }
  } else if (fallbackHour !== undefined) {
    startHour = fallbackHour;
  }

  // Check if any assignment times cross midnight
  const allTimes = assignments?.flatMap((a) => [a.filming_start, a.filming_end]) ?? [];
  const crossesMidnight = hasAfterMidnightTimes(allTimes);

  // If midnight is crossed, extend to 06:00 next day (hour 30)
  const endHour = crossesMidnight ? 30 : 24;

  const hours: HourMarker[] = [];
  let currentTop = 0;

  for (let h = startHour; h < endHour; h++) {
    const displayHour = h >= 24 ? h - 24 : h;
    hours.push({
      hour: h,
      time: `${String(displayHour).padStart(2, "0")}:00`,
      topPosition: currentTop,
    });
    currentTop += hourHeight;
  }

  return { hours, startHour, totalHeight: currentTop };
}

/**
 * Get the pixel position for a time string within the timeline.
 * Converts times before 06:00 to the 24+ range for correct positioning.
 *
 * Returns -1 when the time falls outside the timeline range. Callers
 * (e.g. the current-time marker) use this to hide themselves rather
 * than render at the top of the schedule. Returning 0 for out-of-
 * range used to land the "now" marker on the very first hour line
 * even when the schedule didn't start until hours later.
 */
export function getPositionForTime(
  timeString: string,
  hours: HourMarker[],
  pixelsPerMinute: number = PIXELS_PER_MINUTE,
): number {
  const rawHour = parseInt(timeString.substring(0, 2));
  const minuteInHour = parseInt(timeString.substring(3, 5));

  // Convert after-midnight hours to 24+ range if timeline extends past midnight
  const hour = rawHour < 6 && hours.some((h) => h.hour >= 24) ? rawHour + 24 : rawHour;

  const hourMarker = hours.find((h) => h.hour === hour);
  if (!hourMarker) return -1;

  return hourMarker.topPosition + minuteInHour * pixelsPerMinute;
}

/**
 * Hours strictly before this are treated as "after midnight" — i.e. the
 * tail end of the previous festival day rather than the start of a new one.
 * 06:00 is the convention used across the schedule timelines: a set at
 * 02:00 belongs to the night before, a set at 07:00 is a fresh day.
 */
export const AFTER_MIDNIGHT_THRESHOLD = 6;

/**
 * Parse a time string ("HH", "HH:MM" or "HH:MM:SS") into an absolute hour
 * on a single 24h+ timeline. Hours before `threshold` roll into the 24+
 * range so a 02:00 set sorts AFTER a 23:00 set on the same festival night.
 * Returns null when the string can't be parsed.
 */
export function normalizeTimelineHour(
  time: string | null | undefined,
  threshold: number = AFTER_MIDNIGHT_THRESHOLD,
): number | null {
  if (!time) return null;
  const rawHour = parseInt(time.substring(0, 2), 10);
  if (Number.isNaN(rawHour)) return null;
  return rawHour < threshold ? rawHour + 24 : rawHour;
}

/**
 * Pixel offset of `time` on a HORIZONTAL timeline whose left edge is
 * `startHour` and whose hours are `pixelsPerHour` wide (already multiplied
 * by any zoom factor). One source of truth for the schedule creator, the
 * read-only / client-share timeline and the dashboard preview card, so a
 * block, a feedback pin and the current-time marker can never drift apart.
 *
 * `afterMidnight` (default true) rolls hours before 06:00 into the 24+ range,
 * matching how the timelines anchor a festival night. The dashboard preview
 * card draws a plain same-day axis and opts out.
 *
 * Returns NaN for an unparseable time so callers can hide rather than render
 * at offset 0.
 */
export function horizontalPositionForTime(
  time: string | null | undefined,
  startHour: number,
  pixelsPerHour: number,
  options: { afterMidnight?: boolean } = {},
): number {
  if (!time) return NaN;
  const rawHour = parseInt(time.substring(0, 2), 10);
  if (Number.isNaN(rawHour)) return NaN;
  const minutes = parseInt(time.substring(3, 5), 10);
  // Same roll as normalizeTimelineHour, inlined: this runs per block, per
  // artist and per feedback pin on every render of both timelines, so it
  // parses the string once instead of handing it on to be parsed again.
  const afterMidnight = options.afterMidnight ?? true;
  const hour = afterMidnight && rawHour < AFTER_MIDNIGHT_THRESHOLD ? rawHour + 24 : rawHour;
  const totalMinutes = (hour - startHour) * 60 + (Number.isNaN(minutes) ? 0 : minutes);
  return (totalMinutes / 60) * pixelsPerHour;
}

/**
 * Resolve the hour a horizontal schedule timeline should start at.
 *
 *   doorsOpen      — the selected day's doors-open time ("HH:MM" / null).
 *                    This is the desired start: the planning begins when the
 *                    festival opens, not at a fixed office hour.
 *   itemStartTimes — every scheduled item / lineup start that must stay
 *                    visible. A genuine data inconsistency (an item placed
 *                    before doors) pulls the start back so nothing is ever
 *                    pushed past start+24 and disappears off the timeline.
 *   fallback       — used only when doors are unset AND there are no items
 *                    (default 09:00, the legacy behaviour).
 *
 * Rule: startHour = min( doors-open hour, earliest business-hour item ).
 * After-midnight items (before 06:00) normalise into the 24+ range and so
 * never lower the start — they belong to the night, not the morning.
 */
export function resolveScheduleStartHour(input: {
  doorsOpen?: string | null;
  itemStartTimes?: (string | null | undefined)[];
  fallback?: number;
}): number {
  const { doorsOpen, itemStartTimes = [], fallback = 9 } = input;

  const candidates: number[] = [];

  // Doors define where the day starts, so anchor on the literal doors hour
  // (0-23) — never rolled into the 24+ range, even for an after-midnight
  // opening time.
  const doorsRawHour = doorsOpen ? parseInt(doorsOpen.substring(0, 2), 10) : NaN;
  if (!Number.isNaN(doorsRawHour)) candidates.push(doorsRawHour);

  for (const t of itemStartTimes) {
    const h = normalizeTimelineHour(t);
    // Only same-day (business) items lower the anchor; after-midnight items
    // sit in the tail and must not drag the start back into the morning.
    if (h !== null && h < 24) candidates.push(h);
  }

  if (candidates.length === 0) return fallback;
  return Math.min(...candidates);
}

export interface BlockLayout {
  /** Pixel offset of the block's start from the top of the timeline. */
  top: number;
  /** Pixel height of the block (start→end, floored at minHeight). */
  height: number;
}

/**
 * Compute each schedule block's pixel top + height from its start/end time.
 * A block never extends past the next block's start (minus `gap`), so long
 * or time-overlapping items stack cleanly instead of drawing over each other.
 *
 * That clamp has one blind spot: a block whose successor starts at (nearly)
 * the same moment — e.g. a filming assignment plus its same-time mirror /
 * backup — can't be shrunk (the available space is ≤ 0), so the pair draws
 * fully on top of each other. Opt in to `minVisibleOffset` to resolve this:
 * each block is pushed at least that many px past its predecessor's start
 * before the clamp runs, so every block keeps a visible, tappable sliver.
 * Off by default — existing (vertical / web) consumers keep their current
 * rendering until they're individually validated with it.
 *
 * The math is platform-agnostic, so the mobile `ScheduleTimeline` and the web
 * live-tracking timeline share it — only the rendered element (RN View vs DOM
 * div) differs. `items` must already be sorted ascending by start time.
 */
export function computeBlockLayouts(
  items: Array<{ start: string; end: string }>,
  hours: HourMarker[],
  pixelsPerMinute: number = PIXELS_PER_MINUTE,
  options: { minHeight?: number; gap?: number; minVisibleOffset?: number } = {},
): BlockLayout[] {
  const minHeight = options.minHeight ?? 44;
  const gap = options.gap ?? 4;
  const minVisibleOffset = options.minVisibleOffset ?? 0;

  const raw = items.map((item) => {
    const top = getPositionForTime(item.start, hours, pixelsPerMinute);
    const end = getPositionForTime(item.end, hours, pixelsPerMinute);
    return { top, height: Math.max(end - top, minHeight) };
  });

  if (minVisibleOffset > 0) {
    for (let i = 1; i < raw.length; i++) {
      if (raw[i].top < raw[i - 1].top + minVisibleOffset) {
        raw[i].top = raw[i - 1].top + minVisibleOffset;
      }
    }
  }

  return raw.map((r, idx) => {
    let height = r.height;
    const next = raw[idx + 1];
    if (next) {
      const maxHeight = next.top - r.top - gap;
      if (maxHeight > 0) height = Math.min(height, maxHeight);
    }
    return { top: r.top, height };
  });
}
